import type { ProspectMatterType, ProspectPracticeArea } from '@common/enums';
import type { QuoteTemplateEntry } from '@common/types/quoteTemplates';
import type { UserEntry } from '@common/types/users';
import { getPracticeAreaLabel } from '@common/util';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Link as MuiLink,
    Select,
    type SelectChangeEvent,
    Stack,
    TextField,
    Tooltip,
} from '@mui/material';
import { type Dispatch, memo, type SetStateAction, useEffect, useId, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import AutoCompleteMultiSelect from '@/components/AutoCompleteMultiselect';
import {
    ALL_PROSPECT_PRACTICE_AREAS,
    getPracticeAreaMatterTypeOptions,
} from '@/utils/practiceAreaMatterTypes';
import NewProspectStep from './NewProspectStep';

interface Props {
    users: UserEntry[];
    filteredQuoteTemplates: QuoteTemplateEntry[];
    lawFirmId?: number;
    isTestFirm?: boolean;
    customFileNumber: string;
    handleCustomFileNumberChange(value: string): void;
    customFileNumberError: string;
    isCustomFileNumberRequired: boolean;
    practiceArea: ProspectPracticeArea;
    handlePracticeAreaChange(value: ProspectPracticeArea): void;
    practiceAreaError: string;
    matterType: ProspectMatterType | '';
    handleMatterTypeChange(value: ProspectMatterType): void;
    matterTypeError: string;
    selectedTemplate: number | null;
    handleSelectedTemplateChange(value: number | null): void;
    selectedTemplateError: string;
    isTemplateDetailsLoading: boolean;
    assignedLawyer: number | null;
    handleAssignedLawyerChange(value: number | null): void;
    assignedLawyerError: string;
    ccPerson: string[];
    setCcPerson: Dispatch<SetStateAction<string[]>>;
    resetErrors(): void;
}

export default memo(function NpInitialDetails({
    users,
    filteredQuoteTemplates,
    lawFirmId,
    isTestFirm = false,
    customFileNumber,
    handleCustomFileNumberChange,
    customFileNumberError,
    isCustomFileNumberRequired,
    practiceArea,
    handlePracticeAreaChange,
    practiceAreaError,
    matterType,
    handleMatterTypeChange,
    matterTypeError,
    selectedTemplate,
    handleSelectedTemplateChange,
    selectedTemplateError,
    isTemplateDetailsLoading,
    assignedLawyer,
    handleAssignedLawyerChange,
    assignedLawyerError,
    ccPerson,
    setCcPerson,
    resetErrors,
}: Props) {
    const practiceAreaSelectId = useId();
    const matterTypeSelectId = useId();
    const selectedTemplateSelectId = useId();
    const assignedLawyerSelectId = useId();

    const isStepComplete = assignedLawyer && selectedTemplate && practiceArea && matterType;
    const isTemplateSelectionDisabled = !matterType || isTemplateDetailsLoading;
    const areTemplateDerivedFieldsDisabled = !selectedTemplate || isTemplateDetailsLoading;

    const practiceAreaMatterTypeOptions = useMemo(
        () => getPracticeAreaMatterTypeOptions({ lawFirmId, isTestFirm }),
        [lawFirmId, isTestFirm],
    );

    const matterTypeOptions = useMemo(
        () => (practiceArea ? practiceAreaMatterTypeOptions[practiceArea] : []),
        [practiceArea, practiceAreaMatterTypeOptions],
    );

    useEffect(() => {
        const availableMatterTypeOptions = matterTypeOptions.filter(({ disabled }) => !disabled);

        if (!matterType && availableMatterTypeOptions.length === 1) {
            handleMatterTypeChange(availableMatterTypeOptions[0].value as ProspectMatterType);
        }
    }, [handleMatterTypeChange, matterType, matterTypeOptions]);

    useEffect(() => {
        if (!selectedTemplate && filteredQuoteTemplates.length === 1) {
            handleSelectedTemplateChange(filteredQuoteTemplates[0].quoteTemplateId);
        }
    }, [filteredQuoteTemplates, handleSelectedTemplateChange, selectedTemplate]);

    const availableCcContacts = users
        ?.filter((user) => user.userId !== assignedLawyer)
        ?.map((user) => user.email);

    return (
        <NewProspectStep
            stepNumber="1"
            title="Initial Details"
            description="Set up basic case information and assignments"
            isComplete={!!isStepComplete}
        >
            <Stack spacing={4}>
                <FormControl
                    fullWidth
                    required={isCustomFileNumberRequired}
                    error={!!customFileNumberError}
                >
                    <TextField
                        fullWidth
                        required={isCustomFileNumberRequired}
                        label={
                            isCustomFileNumberRequired
                                ? 'Custom File Number'
                                : 'Custom File Number (optional)'
                        }
                        placeholder={
                            isCustomFileNumberRequired
                                ? 'Enter custom file number'
                                : 'Enter custom file number (optional)'
                        }
                        value={customFileNumber}
                        error={!!customFileNumberError}
                        sx={{ backgroundColor: 'white' }}
                        onChange={(event) => handleCustomFileNumberChange(event.target.value)}
                    />
                    <FormHelperText sx={customFileNumberError ? { color: 'red' } : undefined}>
                        {customFileNumberError ||
                            'Enter your own file number to be displayed in this portal and in emails about the transaction'}
                    </FormHelperText>
                </FormControl>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <FormControl fullWidth required error={!!practiceAreaError}>
                        <InputLabel id={practiceAreaSelectId}>Select Practice Area</InputLabel>
                        <Select
                            labelId={practiceAreaSelectId}
                            label="Select Practice Area"
                            value={practiceArea}
                            sx={{ backgroundColor: 'white' }}
                            onChange={(event: SelectChangeEvent<string>) =>
                                handlePracticeAreaChange(event.target.value as ProspectPracticeArea)
                            }
                        >
                            {ALL_PROSPECT_PRACTICE_AREAS.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {getPracticeAreaLabel(option)}
                                </MenuItem>
                            ))}
                        </Select>
                        <FormHelperText sx={{ color: 'red' }}>{practiceAreaError}</FormHelperText>
                    </FormControl>

                    <ArrowForwardIcon color="action" />

                    <FormControl fullWidth required error={!!matterTypeError}>
                        <InputLabel id={matterTypeSelectId}>Select Matter Type</InputLabel>
                        <Select
                            labelId={matterTypeSelectId}
                            label="Select Matter Type"
                            value={matterType}
                            disabled={!practiceArea}
                            sx={{ backgroundColor: 'white' }}
                            onChange={(event: SelectChangeEvent<string>) =>
                                handleMatterTypeChange(event.target.value as ProspectMatterType)
                            }
                        >
                            {matterTypeOptions.map(({ label, value, disabled }) => (
                                <MenuItem key={value} value={value} disabled={disabled}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                        <FormHelperText sx={{ color: 'red' }}>{matterTypeError}</FormHelperText>
                    </FormControl>

                    <ArrowForwardIcon color="action" />

                    <Tooltip
                        title={
                            !matterType
                                ? 'Please select matter type first'
                                : isTemplateDetailsLoading
                                  ? 'Template details are loading'
                                  : ''
                        }
                        placement="top"
                    >
                        <FormControl fullWidth required error={!!selectedTemplateError}>
                            <InputLabel id={selectedTemplateSelectId}>Select Template</InputLabel>
                            <Select<number>
                                labelId={selectedTemplateSelectId}
                                label="Select Template"
                                value={selectedTemplate ?? ''}
                                disabled={isTemplateSelectionDisabled}
                                sx={{ backgroundColor: 'white' }}
                                onChange={(event: SelectChangeEvent<number>) => {
                                    handleSelectedTemplateChange(
                                        event.target.value === ''
                                            ? null
                                            : Number(event.target.value),
                                    );
                                }}
                            >
                                {filteredQuoteTemplates.map((qt) => (
                                    <MenuItem key={qt.quoteTemplateId} value={qt.quoteTemplateId}>
                                        {qt.quoteTemplateName}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText sx={{ color: 'red' }}>
                                {selectedTemplateError}
                            </FormHelperText>
                        </FormControl>
                    </Tooltip>
                </Stack>

                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Tooltip
                        title={
                            !selectedTemplate
                                ? 'Please select template first'
                                : isTemplateDetailsLoading
                                  ? 'Template details are loading'
                                  : ''
                        }
                        placement="top"
                    >
                        <FormControl
                            required
                            error={!!assignedLawyerError}
                            sx={{ width: 'calc(33% - 6px)' }}
                        >
                            <InputLabel id={assignedLawyerSelectId}>
                                Select Assigned User
                            </InputLabel>
                            <Select<number>
                                labelId={assignedLawyerSelectId}
                                label="Select Assigned User"
                                value={assignedLawyer ?? ''}
                                disabled={areTemplateDerivedFieldsDisabled}
                                sx={{ backgroundColor: 'white' }}
                                onChange={(event: SelectChangeEvent<number>) =>
                                    handleAssignedLawyerChange(
                                        event.target.value === ''
                                            ? null
                                            : Number(event.target.value),
                                    )
                                }
                            >
                                {users.map(({ userId, fullName }) => (
                                    <MenuItem key={userId} value={userId}>
                                        {fullName}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText sx={assignedLawyerError ? { color: 'red' } : undefined}>
                                {assignedLawyerError ||
                                    'Emails will be sent on behalf of this lawyer or clerk'}
                            </FormHelperText>
                        </FormControl>
                    </Tooltip>

                    <Tooltip
                        title={
                            !selectedTemplate
                                ? 'Please select template first'
                                : isTemplateDetailsLoading
                                  ? 'Template details are loading'
                                  : ''
                        }
                        placement="top"
                    >
                        <FormControl sx={{ width: 'calc(67% - 6px)' }}>
                            <AutoCompleteMultiSelect
                                fullWidth
                                options={availableCcContacts}
                                getOptionLabel={(option) => option}
                                label="CC List"
                                value={ccPerson}
                                onChange={(_event, value) => {
                                    resetErrors();
                                    setCcPerson(value);
                                }}
                                disabled={areTemplateDerivedFieldsDisabled}
                                sx={{ backgroundColor: 'white' }}
                            />
                            <FormHelperText>
                                Additional lawyers or clerks to be CC'd in client emails (opt out of
                                notifications in{' '}
                                <MuiLink component={RouterLink} to="/settings/notification">
                                    Settings
                                </MuiLink>
                                )
                            </FormHelperText>
                        </FormControl>
                    </Tooltip>
                </Stack>
            </Stack>
        </NewProspectStep>
    );
});
